import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import IconsSend from '@mui/icons-material/Send';
import CircularProgress from '@mui/material/CircularProgress';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import useNotification from '../../../../components/_hook/useNotification';
import ButtonSubmit from '../../../../components/_button/ButtonSubmit';
import useForm from '../../../../components/_hook/useForm';
import useLanguage from '../../../../components/_hook/useLanguage';
import { useAllowDropshippingProductsMutation } from '../../../../../graphql/mutation/product/allowDropshippingProductsMutation';
import { useProductQuery } from '../../../../../graphql/query/product/useProductQuery';
import Context from '../../../../context';
import { useUpdateProductInformationMutation } from '../../../../../graphql/mutation/product/updateProductInformationMutation';
import SelectCurrency from '../../../../components/_select/SelectCurrency';
import { IValue } from '../../../../components/_select/_module/Select';

interface IProps {
  productIDs: string[];
  onCompleted: () => void;
  onClose: () => void;
}

export default function FormAllowDropshipping(props: IProps) {
  const { t } = useTranslation();
  const { notification } = useNotification();
  const { getText } = useLanguage();
  const { company } = useContext(Context);
  const { onClose, onCompleted, productIDs } = props;
  const [currentProductIndex, setCurrentProductIndex] = useState(0);

  interface IFormValue {
    priceCurrency: string;
    priceFinal: number;
  }

  const productQueryFragment = `
            _id
            status
            featured
            dropshipping {
              enable
            }
            import_id
            ordering
            condition
            categoryOrdering
            name {
              la
              text
            }
            infos {
              intro {
                la
                text
              }
              unit {
                la
                text
              }
            }
            origin
            sameAs
            keywords {
              classifications
              classificationsData {
                _id
                name
              }
              brands
              brandsData {
                _id
                name
              }
              tags
              tagsData {
                _id
                name
              }
            }
            prices {
              isHidePrice
              currency
              cost
              base
              final
              regular
              override
            }
  `;
  const { data, loading } = useProductQuery(productQueryFragment, {
    variables: {
      filter: {
        companyId: company._id,
        _ids: productIDs,
      },
    },
  });
  const products = data ? data.products.items : [];
  const [
    allowDropshippingProductsMutation,
  ] = useAllowDropshippingProductsMutation(
    `updatedProducts {
				_id
				dropshipping {
				  enable
				}
			}`,
    {
      onCompleted: () => {
        notification.success(t('allow_dropshipping_done'));
        if (currentProductIndex === products.length - 1) {
          onCompleted();
        } else {
          setCurrentProductIndex(currentProductIndex + 1);
        }
      },
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );

  const [
    updateProductInformationMutation,
  ] = useUpdateProductInformationMutation(
    `updatedProduct {
				${productQueryFragment}
			}`,
    {
      onCompleted: async (tempData) => {
        notification.success(t('update_product_price_done'));

        if (
          tempData.updateProductInformation &&
          tempData.updateProductInformation.updatedProduct &&
          tempData.updateProductInformation.updatedProduct._id
        ) {
          await allowDropshippingProductsMutation({
            variables: {
              _ids: [tempData.updateProductInformation.updatedProduct._id],
            },
          });
        }
      },
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );

  async function updateProductInformation(values: IFormValue) {
    const product = products[currentProductIndex];

    const classificationsData = product.keywords.classificationsData || [];
    const brandsData = product.keywords.brandsData || [];
    const tagsData = product.keywords.tagsData || [];

    await updateProductInformationMutation({
      variables: {
        _id: product._id,
        status: product.status,
        featured: product.featured,
        enableDropshipping: product.dropshipping
          ? Boolean(product.dropshipping.enable)
          : false,

        importId: product.import_id || '',
        ordering: product.ordering,
        condition: product.condition,
        categoryOrdering: product.categoryOrdering,

        name: product.name.map((label) => ({
          text: label.text,
          la: label.la,
        })),
        introduction: product.infos.intro
          ? product.infos.intro.map((label) => ({
              text: label.text,
              la: label.la,
            }))
          : [],
        unit: product.infos.unit
          ? product.infos.unit.map((label) => ({
              text: label.text,
              la: label.la,
            }))
          : [],
        origin: product.origin,
        referenceUrl: product.sameAs,

        classifications: classificationsData.map(
          (classification) => classification.name
        ),
        brands: brandsData.map((brand) => brand.name),
        tags: tagsData.map((tag) => tag.name),

        hideDisplayPrice: product.prices.isHidePrice || false,
        currency: values.priceCurrency,
        costPrice: product.prices.cost || 0,
        basePrice: product.prices.base || 0,
        finalPrice: values.priceFinal,
        regularPrice: product.prices.regular || 0,
        overridePrice: product.prices.override || 0,
      },
    });
  }

  const {
    values,
    errors,
    handleValidation,
    setValue,
    handleSubmit,
    isSubmitting,
  } = useForm<IFormValue>({
    defaultValues: {
      priceCurrency: '',
      priceFinal: 0,
    },
    onValidate: (tempValues) => {
      const tempErrors: Record<string, string> = {};

      if (!tempValues.priceCurrency) {
        tempErrors.priceCurrency = t('please_enter_product_currency');
      }

      if (!tempValues.priceFinal) {
        tempErrors.priceFinal = t('please_enter_product_price');
      }

      return tempErrors;
    },
    onSubmit: updateProductInformation,
  });

  useEffect(() => {
    const product = products[currentProductIndex];

    if (product && product.prices) {
      setValue('priceCurrency', product.prices.currency || '');
      setValue('priceFinal', product.prices.final || 0);
    }
  }, [currentProductIndex]);

  const currentProduct =
    currentProductIndex <= products.length - 1
      ? products[currentProductIndex]
      : null;

  if (loading) {
    return (
      <Grid container justifyContent="center">
        <CircularProgress size={30} color="inherit" />
      </Grid>
    );
  }
  return (
    <>
      <DialogTitle>
        <Typography
          paragraph
          gutterBottom
          variant="h6"
          color="initial"
          align="center"
        >
          {t('allow_dropshipping')}
        </Typography>
        <Typography variant="subtitle1" color="initial" align="center">
          {t('please_enter_product_sales_price_to_enable_dropshipping')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          {currentProduct ? (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle1" color="initial">
                  {t('product_name')}: {getText(currentProduct.name)}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <SelectCurrency<IValue>
                  isClearable
                  menuPosition="fixed"
                  fullWidth
                  required
                  name="priceCurrency"
                  label={t('currency')}
                  value={values.priceCurrency}
                  queryVariables={{
                    filter: {},
                  }}
                  onChange={(option) => {
                    setValue('priceCurrency', option ? option.value : '');
                  }}
                  error={Boolean(errors.priceCurrency)}
                  helperText={errors.priceCurrency}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  type="number"
                  fullWidth
                  required
                  name="priceFinal"
                  label={t('selling_price')}
                  value={values.priceFinal}
                  onChange={(e) =>
                    setValue('priceFinal', parseFloat(e.target.value))
                  }
                  error={Boolean(errors.priceFinal)}
                  helperText={errors.priceFinal}
                  onBlur={handleValidation}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {values.priceCurrency}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </>
          ) : (
            <Grid item xs={12}>
              <Typography variant="subtitle1" color="initial" align="center">
                {t('please_select_at_least_one__name__', {
                  name: t('product').toLowerCase(),
                })}
              </Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Grid container spacing={2} justifyContent="flex-end">
          <Grid item>
            <Button variant="text" onClick={onClose}>
              {t('cancel')}
            </Button>
          </Grid>
          <Grid item>
            <ButtonSubmit
              variant="contained"
              color="primary"
              isSubmitting={isSubmitting}
              onClick={handleSubmit}
              label={
                currentProductIndex === products.length - 1
                  ? t('save_and_complete')
                  : t('save_and_next')
              }
              icon={<IconsSend />}
            />
          </Grid>
        </Grid>
      </DialogActions>
    </>
  );
}
